import { App, Nullable } from '../interfaces';
import { PresentedData, PresentedView, Presenter } from './Presentation';
import {
    GuideItem,
    GuideSection,
    GuidesManifest,
    loadGuideArticle,
    loadGuideThumbnails,
    loadGuidesManifest,
} from './Guides';

export type GuidesStatus = 'loading' | 'ready' | 'error';
export type GuideArticleStatus = 'loading' | 'ready' | 'error';

export class HowToGuidesPresenter implements Presenter {
    private app: App;
    private view: Nullable<PresentedView>;
    private status: GuidesStatus = 'loading';
    private errorMessage: string = '';
    private manifest: Nullable<GuidesManifest>;
    private thumbnailUrlById: Record<string, string> = {};
    private selectedItemId: Nullable<string>;
    private articleStatus: GuideArticleStatus = 'loading';
    private articleMarkdown: string = '';
    // The section list is fetched once per visit; a browser refresh picks up edits.
    private hasStartedLoading: boolean = false;

    constructor(options: { app: App }) {
        const { app } = options;
        if (!app) {
            throw new Error('HowToGuidesPresenter(): missing app parameter');
        }
        this.app = app;
    }

    presentedData(): PresentedData {
        const sections = this.manifest?.sections ?? [];
        const selected = this.findSelected();
        return {
            status: this.status,
            errorMessage: this.errorMessage,
            hero: this.manifest?.hero,
            sections: sections,
            thumbnailUrlById: this.thumbnailUrlById,
            selectedItem: selected.item,
            selectedSectionTitle: selected.section?.title ?? '',
            articleStatus: this.articleStatus,
            articleMarkdown: this.articleMarkdown,
            onOpenGuide: (itemId: string) => this.openGuide(itemId),
            onCloseGuide: () => this.closeGuide(),
            onRetry: () => this.loadGuides(),
        };
    }

    viewWillUpdate(view: PresentedView): void {
        this.view = view;
        if (!this.hasStartedLoading) {
            this.hasStartedLoading = true;
            this.loadGuides();
        }
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    // Look up the currently open card, and the section it belongs to.
    private findSelected(): { item: Nullable<GuideItem>; section: Nullable<GuideSection> } {
        if (!this.selectedItemId || !this.manifest) {
            return { item: null, section: null };
        }
        const sections: Array<GuideSection> = this.manifest.sections ?? [];
        for (const section of sections) {
            const item = section.items.find((candidate) => candidate.id === this.selectedItemId);
            if (item) {
                return { item, section };
            }
        }
        return { item: null, section: null };
    }

    // Load the section list, then resolve every card thumbnail.
    private async loadGuides(): Promise<void> {
        this.status = 'loading';
        this.errorMessage = '';
        this.safeUpdatePresentedData();

        try {
            const manifest = await loadGuidesManifest();
            this.manifest = manifest;
            this.thumbnailUrlById = await loadGuideThumbnails(manifest.sections ?? []);
            this.status = 'ready';
        } catch (error) {
            console.log('HOW_TO_GUIDES_PRESENTER: loadGuides() failed:', error);
            this.status = 'error';
            this.errorMessage = (error as Error)?.message ?? 'The guides could not be loaded.';
        }
        this.safeUpdatePresentedData();
    }

    // Open a card: PDFs need no fetch, articles load their Markdown.
    private async openGuide(itemId: string): Promise<void> {
        this.selectedItemId = itemId;
        this.articleMarkdown = '';

        const { item } = this.findSelected();
        if (!item || item.type !== 'article' || !item.article) {
            this.articleStatus = 'ready';
            this.safeUpdatePresentedData();
            return;
        }

        this.articleStatus = 'loading';
        this.safeUpdatePresentedData();

        try {
            const markdown = await loadGuideArticle(item.article);
            // Ignore a response that arrives after the reader moved on.
            if (this.selectedItemId !== itemId) {
                return;
            }
            this.articleMarkdown = markdown;
            this.articleStatus = 'ready';
        } catch (error) {
            console.log('HOW_TO_GUIDES_PRESENTER: openGuide() failed:', error);
            if (this.selectedItemId !== itemId) {
                return;
            }
            this.articleStatus = 'error';
        }
        this.safeUpdatePresentedData();
    }

    // Return to the list of guides.
    private closeGuide(): void {
        this.selectedItemId = null;
        this.articleMarkdown = '';
        this.articleStatus = 'loading';
        this.safeUpdatePresentedData();
    }

    private safeUpdatePresentedData() {
        const data = this.presentedData();
        if (this.view) {
            this.view.updatePresentedData(data);
        }
    }
}
